import "./App.css";
import StickerApp from "./components/StickerApp";
import Viewer from "./components/Viewer/Viewer";
import PaymentSuccess from "./pages/PaymentSuccess";
import PaymentCancel from "./pages/PaymentCancel";
import DashboardPage from "./pages/DashboardPage";
import ExplorePage from "./pages/ExplorePage";
import GuideLandingPage from "./pages/GuideLandingPage";
import DestinosPage from "./pages/DestinosPage";
import PassportLandingPage from "./pages/PassportLandingPage";
import RutasPage from "./pages/RutasPage";
import RutasIndexPage from "./pages/RutasIndexPage";
import RouteBuilderPage from "./pages/RouteBuilderPage";
import ScrapbookPage from "./pages/ScrapbookPage";
import MyGuidesPage from "./pages/MyGuidesPage";
import FavoritosPage from "./pages/FavoritosPage";
import ProfilePage from "./pages/ProfilePage";
import DiscountsPage from "./pages/DiscountsPage";
import PassportPage from "./pages/PassportPage";
import PassportViewerPage from "./pages/PassportViewerPage";
import TravelCardPage from "./pages/TravelCardPage";
import ModulesPage from "./pages/ModulesPage";
import LegalAviso from "./pages/legal/LegalAviso";
import LegalPrivacidad from "./pages/legal/LegalPrivacidad";
import LegalTerminos from "./pages/legal/LegalTerminos";
import LegalCookies from "./pages/legal/LegalCookies";
import VerifyEmailPage from "./pages/VerifyEmailPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import ProtectedRoute from "./components/ProtectedRoute";
import EmailVerificationBanner from "./components/EmailVerificationBanner";
import CookieBanner from "./components/CookieBanner";
import CookiePreferencesModal from "./components/CookiePreferencesModal";
import { ApiProvider } from "./context/ApiContext";
import { CookieConsentProvider } from "./context/CookieConsentContext";
import GlobalSplashScreen from "./components/GlobalSplashScreen";
import OrientationGuard from "./components/OrientationGuard";
import { createTheme, ThemeProvider } from "@mui/material";
import { styled } from "@mui/material/styles";
import { SnackbarProvider, MaterialDesignContent } from "notistack";
import {
	BrowserRouter,
	Routes,
	Route,
	Navigate,
	useLocation,
	useParams,
} from "react-router-dom";
import { useEffect } from "react";
import { trackSmartPageView } from "./utils/analytics";
import { HelmetProvider } from "react-helmet-async";

function RedirectLegacyGuiaPath() {
	const { slug } = useParams();
	return <Navigate to={`/${slug}`} replace />;
}

function RouteTracker() {
	const location = useLocation();
	useEffect(() => {
		trackSmartPageView(location.pathname);
		window.scrollTo({ top: 0, left: 0, behavior: "auto" });
		document.documentElement.scrollTop = 0;
		document.body.scrollTop = 0;
	}, [location.pathname]);
	return null;
}

const theme = createTheme({
	breakpoints: {
		values: {
			xs: 0,
			sm: 600,
			md: 921, // ≤920px = móvil (header glass, hero full-bleed)
			lg: 1200,
			xl: 1536,
		},
	},
	palette: {
		primary: {
			main: "#6366F1",
			dark: "#4F46E5",
		},
	},
	components: {
		MuiButton: {
			styleOverrides: {
				containedPrimary: {
					backgroundColor: "#6366F1",
					"&:hover": {
						backgroundColor: "#4F46E5",
					},
				},
			},
		},
	},
});

/* Toasts modernos: centrados, redondeados, con sombra y colores por variante */
const StyledSnackbar = styled(MaterialDesignContent)(({ theme }) => ({
	"&.notistack-MuiContent-default": {
		backgroundColor: theme.palette.grey[800],
		color: theme.palette.common.white,
	},
	"&.notistack-MuiContent-success": {
		backgroundColor: "#0d9488",
		color: "#fff",
	},
	"&.notistack-MuiContent-error": {
		backgroundColor: "#dc2626",
		color: "#fff",
	},
	"&.notistack-MuiContent-warning": {
		backgroundColor: "#d97706",
		color: "#fff",
	},
	"&.notistack-MuiContent-info": {
		backgroundColor: theme.palette.primary.main,
		color: "#fff",
	},
	"&.notistack-MuiContent-success,&.notistack-MuiContent-error,&.notistack-MuiContent-warning,&.notistack-MuiContent-info,&.notistack-MuiContent-default":
		{
			borderRadius: 12,
			boxShadow: "0 4px 24px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.08)",
			padding: "12px 20px",
			fontSize: "0.9375rem",
			fontWeight: 500,
			minWidth: 288,
			maxWidth: "min(400px, calc(100vw - 32px))",
		},
}));

function App() {
	return (
		<ThemeProvider theme={theme}>
			<SnackbarProvider
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
				autoHideDuration={4000}
				maxSnack={3}
				Components={{
					default: StyledSnackbar,
					success: StyledSnackbar,
					error: StyledSnackbar,
					warning: StyledSnackbar,
					info: StyledSnackbar,
				}}
			>
				<ApiProvider>
					<CookieConsentProvider>
						<HelmetProvider>
							<GlobalSplashScreen />
							<OrientationGuard />
							<BrowserRouter>
								<RouteTracker />
								<EmailVerificationBanner />
								<CookieBanner />
								<CookiePreferencesModal />
								<Routes>
								{/* Verificación de email */}
								<Route path="/verify-email" element={<VerifyEmailPage />} />
								{/* Olvidar / restablecer contraseña */}
								<Route
									path="/forgot-password"
									element={<ForgotPasswordPage />}
								/>
								<Route path="/reset-password" element={<ResetPasswordPage />} />
								{/* Home (antes /dashboard) */}
								<Route path="/" element={<DashboardPage />} />
								<Route
									path="/dashboard"
									element={<Navigate to="/" replace />}
								/>
								<Route
									path="/inicio"
									element={<Navigate to="/" replace />}
								/>
								{/* Rutas públicas */}
								<Route path="/explorar" element={<ExplorePage />} />
								<Route path="/destinos" element={<DestinosPage />} />
								<Route path="/crear-guia" element={<StickerApp />} />
								<Route
									path="/guia/:slug"
									element={<RedirectLegacyGuiaPath />}
								/>
								<Route path="/pasaporte" element={<PassportLandingPage />} />
								<Route path="/rutas/tokio" element={<RutasPage />} />
								<Route path="/rutas" element={<RutasIndexPage />} />
								<Route path="/crear-ruta" element={<RouteBuilderPage />} />
								<Route path="/album" element={<ScrapbookPage />} />
								<Route
									path="/seoul"
									element={<Navigate to="/seul" replace />}
								/>
								<Route
									path="/pricing"
									element={
										<Navigate
											to="/profile"
											state={{ section: "plan" }}
											replace
										/>
									}
								/>
								<Route
									path="/descuentos"
									element={
										<ProtectedRoute>
											<DiscountsPage />
										</ProtectedRoute>
									}
								/>
								<Route path="/payment/success" element={<PaymentSuccess />} />
								<Route path="/payment/cancel" element={<PaymentCancel />} />

								{/* Viewer para guías compartidas (público) */}
								<Route path="/view/:token/:pageNum?" element={<Viewer />} />
								{/* Viewer para pasaportes compartidos (público) */}
								<Route
									path="/passport/view/:token"
									element={<PassportViewerPage />}
								/>
								{/* Vista previa de guías prebuilt (pública: N páginas) */}
								<Route
									path="/preview/prebuilt/:guideId/:pageNum?"
									element={<Viewer />}
								/>

								{/* Páginas legales (no indexar, enlaces con rel="nofollow") */}
								<Route path="/legal/aviso-legal" element={<LegalAviso />} />
								<Route path="/legal/privacidad" element={<LegalPrivacidad />} />
								<Route path="/legal/terminos" element={<LegalTerminos />} />
								<Route path="/legal/cookies" element={<LegalCookies />} />

								{/* Rutas protegidas - requieren autenticación */}
								<Route
									path="/profile"
									element={
										<ProtectedRoute>
											<ProfilePage />
										</ProtectedRoute>
									}
								/>
								<Route
									path="/settings"
									element={
										<ProtectedRoute>
											<ProfilePage />
										</ProtectedRoute>
									}
								/>
								<Route
									path="/mis-guias"
									element={
										<ProtectedRoute>
											<MyGuidesPage />
										</ProtectedRoute>
									}
								/>
								<Route
									path="/favoritos"
									element={
										<ProtectedRoute>
											<FavoritosPage />
										</ProtectedRoute>
									}
								/>
								<Route path="/passport" element={<PassportPage />} />
								<Route
									path="/tarjeta-viajera"
									element={<TravelCardPage />}
								/>
								<Route
									path="/modulos"
									element={
										<ProtectedRoute>
											<ModulesPage />
										</ProtectedRoute>
									}
								/>
								{/* Ruta logros-viajeros comentada
					<Route
						path="/logros-viajeros"
						element={
							<ProtectedRoute>
								<LogrosViajerosPage />
							</ProtectedRoute>
						}
					/>
					*/}
								<Route
									path="/usuarios/:userId/guias/:projectId"
									element={
										<ProtectedRoute>
											<StickerApp />
										</ProtectedRoute>
									}
								/>

								<Route
									path="/home"
									element={<Navigate to="/" replace />}
								/>
								<Route
									path="/explore"
									element={<Navigate to="/explorar" replace />}
								/>
								<Route path="/:slug" element={<GuideLandingPage />} />
								<Route
									path="*"
									element={<Navigate to="/" replace />}
								/>
								</Routes>
							</BrowserRouter>
						</HelmetProvider>
					</CookieConsentProvider>
				</ApiProvider>
			</SnackbarProvider>
		</ThemeProvider>
	);
}

export default App;
